/*
 * SPDX-FileCopyrightText: Copyright (c) 2024 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-NvidiaProprietary
 *
 * NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
 * property and proprietary rights in and to this material, related
 * documentation and any modifications thereto. Any use, reproduction,
 * disclosure or distribution of this material and related documentation
 * without an express license agreement from NVIDIA CORPORATION or
 * its affiliates is strictly prohibited.
 */

import { isJupyterTeardownRejection } from '@/lib/utils/jupyterCanceledFutureFilter';
import { isRerunWasmRuntimeError, isRerunWasmSentryEvent, isRerunWasmTeardownError } from '@/lib/utils/rerunWasmFilter';

// Auto-reload on stale chunk errors after a new deployment. Vite emits
// vite:preloadError when a dynamically imported chunk fails to load
// (e.g. after Cloudflare Pages deploys new assets with different hashes).
// Session storage prevents infinite reload loops.
const STALE_RELOAD_KEY = 'vite-stale-chunk-reload';
const STALE_RELOAD_COOLDOWN_MS = 30_000;

window.addEventListener('vite:preloadError', () => {
  const now = Date.now();
  const lastReloadRaw = sessionStorage.getItem(STALE_RELOAD_KEY);
  const lastReload = lastReloadRaw ? Number(lastReloadRaw) : 0;

  if (!lastReload || Number.isNaN(lastReload) || now - lastReload > STALE_RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem(STALE_RELOAD_KEY, String(now));
    window.location.reload();
  }
});

// Suppress known third-party library promise rejections that are not
// actionable bugs. Must be registered BEFORE Sentry.init() so these
// handlers fire first and can preventDefault() before Sentry captures them.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;

  // NVIDIA streaming library rejects with a plain object
  // {action: "terminate", info: "Stream is already terminating.", status: "error"}
  // when terminate() races
  if (
    reason &&
    typeof reason === 'object' &&
    !Array.isArray(reason) &&
    Object.getPrototypeOf(reason) === Object.prototype &&
    (reason as Record<string, unknown>).action === 'terminate' &&
    (reason as Record<string, unknown>).status === 'error' &&
    typeof (reason as Record<string, unknown>).info === 'string' &&
    ((reason as Record<string, unknown>).info as string).toLowerCase().includes('terminat')
  ) {
    event.preventDefault();
    console.warn('[AppStream] suppressed NVIDIA library internal rejection:', reason);
    return;
  }

  // @rerun-io/web-viewer WASM teardown and stopped-viewer errors
  if (isRerunWasmTeardownError(reason)) {
    event.preventDefault();
    console.warn('[Rerun] suppressed WASM teardown error:', reason instanceof Error ? reason.message : reason);
    return;
  }

  // @jupyterlab/services teardown rejections: canceled futures (when a
  // kernel connection is disposed mid-message) and "Kernel connection
  // disconnected" (when a pending kernel.reconnect() is rejected because
  // the kernel was disposed before reconnection completed). Both are
  // expected during kernel restart, watchdog teardown, workspace
  // switching, and page unload -- not application bugs.
  if (isJupyterTeardownRejection(reason)) {
    event.preventDefault();
    return;
  }
});

// @rerun-io/web-viewer WASM runtime panics: Rust panics inside the Rerun
// WASM binary surface as synchronous `RuntimeError: unreachable` via
// requestAnimationFrame. These originate from wgpu/egui internals (e.g.
// GPU buffer mapping failures) and are not actionable.
window.addEventListener('error', (event) => {
  if (isRerunWasmRuntimeError(event.error)) {
    event.preventDefault();
    console.warn('[Rerun] suppressed WASM runtime panic:', event.error.message);
    return;
  }
});

// Initialize Sentry before anything else to capture all errors
import * as Sentry from '@sentry/react';
import { SENTRY_IGNORE_PATTERNS } from './lib/sentry/ignorePatterns';
import { isStaleChunkError } from './lib/utils/isStaleChunkError';
import { isXtermInternalError } from './lib/utils/sentryFilters';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
// Use Vite's canonical dev flag rather than hostname-sniffing — covers
// `localhost`, the `local.console.antioch.com` HTTPS alias, and any
// Tailscale-served dev URL. Production builds always have `DEV === false`,
// so this only gates dev-mode noise.
const isLocalDev = import.meta.env.DEV;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: isLocalDev ? 'local' : import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
    enableLogs: true,
    integrations: [
      // Session Replay (rrweb DOM recording) and full tracing are pure overhead
      // in local dev — you don't replay local sessions, and rrweb recording the
      // live sim clock's ~10 Hz DOM mutations is a real memory/CPU sink. Keep
      // them for deployed builds; locally, just capture errors + console.
      ...(isLocalDev ? [] : [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    ignoreErrors: SENTRY_IGNORE_PATTERNS,
    // Small user base -- capture all traces so no performance issues go unnoticed
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Drop errors originating entirely within @xterm/xterm minified code
      if (isXtermInternalError(event)) return null;

      // Drop stale-chunk errors caused by Cloudflare Pages deploying new
      // assets while users still have the old HTML cached. The
      // vite:preloadError handler above reloads the page automatically;
      // these errors are deployment noise, not application bugs.
      const exValues = event.exception?.values;
      if (exValues?.some((ex) => isStaleChunkError(ex.type, ex.value))) {
        return null;
      }

      // Drop Rerun WASM errors (teardown, stopped viewer, runtime panics)
      // that slip past global handlers
      const exceptionValues = event.exception?.values;
      if (exceptionValues && isRerunWasmSentryEvent(exceptionValues, event.breadcrumbs ?? [])) {
        return null;
      }

      // Attach recent diagnostic ring-buffer snapshots to every error event
      // so Sentry reports carry the full timeline leading up to the issue
      try {
        const { streamLogger } = require('./lib/services/streamLogger') as typeof import('./lib/services/streamLogger');
        const { networkLogger } =
          require('./lib/services/networkLogger') as typeof import('./lib/services/networkLogger');
        const recentStreamErrors = streamLogger.getRecentErrors().slice(-20);
        const recentNetworkFailures = networkLogger.getFailedRequests().slice(-10);
        if (recentStreamErrors.length > 0 || recentNetworkFailures.length > 0) {
          event.extra = {
            ...event.extra,
            recentStreamErrors,
            recentNetworkFailures,
          };
        }
      } catch {
        // Loggers not yet initialized -- skip enrichment
      }
      return event;
    },
  });
}

// Initialize diagnostic loggers before any other code runs
import { consoleLogger } from './lib/services/consoleLogger';
import { networkLogger } from './lib/services/networkLogger';

consoleLogger.initialize();
networkLogger.initialize();

import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import './index.css';
import favicon from './assets/favicon/favicon.ico';
import { AppProviders } from './providers';

// Add favicon to <head>
const linkEl = document.querySelector("link[rel='icon']") || document.createElement('link');
linkEl.setAttribute('rel', 'icon');
linkEl.setAttribute('href', favicon);
if (!linkEl.parentNode) {
  document.head.appendChild(linkEl);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  // NOTE: StrictMode disabled due to incompatibility with @nvidia streaming library
  // The library doesn't handle rapid mount/unmount/remount cycles properly
  // <React.StrictMode>
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <AppProviders>
      <App />
    </AppProviders>
  </Sentry.ErrorBoundary>
  // </React.StrictMode>
);
